import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download, ExternalLink, Mail } from "lucide-react";
import { SOCIALS, HOST_PHOTO_1, HOST_PHOTO_2 } from "@/data/site";
import EpisodeArtwork from "@/components/podcast/EpisodeArtwork";
import PodcastCover from "@/components/podcast/PodcastCover";
import HostCard from "@/components/podcast/HostCard";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";

export default function Media() {
  const [press, setPress] = useState([]);

  useEffect(() => {
    api.get("/press-mentions").then((r) => setPress(r.data));
  }, []);

  const downloadPressKit = () => {
    const content = `PROVE YOUR POINT PODCAST · PRESS KIT
=======================================
Host: Manuel Santana
Show: Prove Your Point Podcast
Based: New York City · Filmed in NYC & Boston
Live: Mondays 7PM – 9PM EST

BIO
---
Manuel Santana is a podcaster, cultural commentator, and the unfiltered
voice behind Prove Your Point. The show is dedicated to honest long-form
conversations on relationships, intersexual dynamics, dating, marriage,
masculine and feminine polarity, and modern love. Filmed in New York City
and Boston.

CONTACT
-------
Booking & Press: ${SOCIALS.email}
Host Instagram: @iammanuelsantana
Show Instagram: ${SOCIALS.instagram}
YouTube: ${SOCIALS.youtube}
IMDb: ${SOCIALS.imdb}

Generated: ${new Date().toISOString()}
`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prove-your-point-press-kit.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Brand assets shown in the gallery — official press photos + show artwork.
  const brandAssets = [
    { type: "photo", label: "Press Photo · 01", url: HOST_PHOTO_1 },
    { type: "photo", label: "Press Photo · 02", url: HOST_PHOTO_2 },
    { type: "cover", label: "Show Cover · Default", tag: "Season 2 · Coming Soon" },
    { type: "cover", label: "Show Cover · Live Mondays", tag: "Live · Mondays 7PM EST" },
  ];

  return (
    <div data-testid="media-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative">
      <StudioBackdrop opacity={0.08} size={260} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E10600] mb-3">
              Media & Press
            </p>
            <h1 className="font-display text-6xl md:text-8xl uppercase tracking-tight leading-[0.9]">
              Press <span className="text-[#E10600]">Kit</span>
            </h1>
            <p className="mt-6 text-zinc-400 max-w-2xl">
              Everything you need to feature Manuel Santana and the Prove Your Point
              Podcast — bio, photos, key stats, and press history.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <button
              data-testid="press-kit-download"
              onClick={downloadPressKit}
              className="btn-primary"
            >
              <Download className="w-4 h-4" />
              Download Press Kit
            </button>
          </div>
        </div>

        {/* Bio block */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20 border-t border-[#262626] pt-16">
          <div className="lg:col-span-5">
            <div data-testid="media-host-card">
              <HostCard
                name="Manuel Santana"
                role="Host · Founder"
                photo={HOST_PHOTO_1}
                location="NYC · Boston"
              />
            </div>
            <p className="mt-4 text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
              High-res press assets available · Email{" "}
              <a href={`mailto:${SOCIALS.email}`} className="text-[#E10600] hover:underline">
                {SOCIALS.email}
              </a>
            </p>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-5xl uppercase leading-none">
              Full <span className="text-[#E10600]">Bio</span>
            </h2>
            <div className="mt-6 space-y-5 text-zinc-300">
              <p>
                <strong className="text-white">Manuel Santana</strong> is the founder
                and host of the Prove Your Point Podcast — an independent long-form
                interview series focused on relationships, intersexual dynamics, and
                what men and women actually want from each other in the modern world.
                Based in <strong className="text-white">New York City</strong> with
                regular shoots in <strong className="text-white">Boston</strong>.
              </p>
              <p>
                With a sharp interview style and a refusal to soften the hard
                questions, Manuel has built one of the most loyal independent
                audiences in the relationship podcast space, drawing dating coaches,
                marriage counselors, fathers, single women, and content creators into
                honest conversations on both sides of the aisle.
              </p>
              <p>
                Now in Season 2, Prove Your Point streams live every Monday from 7PM
                to 9PM EST across YouTube, Spotify, and Apple Podcasts.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626] max-w-md">
              <Stat label="Host IG" value="@iammanuelsantana" />
              <Stat label="Based" value="New York City" />
              <Stat label="Filmed In" value="NYC · Boston" />
              <Stat label="Premiere" value="S2 · June 22" />
            </div>
          </div>
        </section>

        {/* Brand Assets */}
        <section className="mb-20 border-t border-[#262626] pt-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <h2 className="font-display text-5xl uppercase leading-none">
              Brand <span className="text-[#E10600]">Assets</span>
            </h2>
            <p className="text-sm text-zinc-500 max-w-sm">
              Press photos and show artwork — approved for editorial use.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#262626] border border-[#262626]">
            {brandAssets.map((asset, i) => (
              <div
                key={i}
                data-testid={`media-asset-${i}`}
                className="relative bg-[#0A0A0A] group overflow-hidden aspect-square"
              >
                {asset.type === "cover" ? (
                  <PodcastCover episodeTag={asset.tag} />
                ) : asset.type === "artwork" ? (
                  <EpisodeArtwork episode={asset.ep} size="sm" />
                ) : (
                  <img
                    src={asset.url}
                    alt={asset.label}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                )}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/95 to-transparent">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-[#E10600]">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="text-sm font-bold text-white mt-0.5">{asset.label}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Press mentions */}
        {press.length > 0 && (
        <section className="border-t border-[#262626] pt-16">
          <h2 className="font-display text-5xl uppercase leading-none mb-8">
            Press <span className="text-[#E10600]">Mentions</span>
          </h2>
          <div className="space-y-px bg-[#262626] border border-[#262626]">
            {press.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`press-mention-${i}`}
                className="block bg-[#0A0A0A] hover:bg-[#121212] p-6 md:p-8 group transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 text-xs tracking-[0.2em] uppercase text-[#E10600] font-mono">
                      <span>{p.outlet}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="text-zinc-500">{p.date}</span>
                    </div>
                    <h3 className="mt-2 font-display text-2xl md:text-3xl uppercase leading-tight group-hover:text-[#E10600] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-zinc-400 max-w-3xl">"{p.excerpt}"</p>
                  </div>
                  <ExternalLink className="w-6 h-6 text-zinc-500 group-hover:text-[#E10600] transition-colors flex-shrink-0" />
                </div>
              </a>
            ))}
          </div>
        </section>
        )}

        <section className="mt-12 border-t border-[#262626] pt-12">
          <div className="p-6 md:p-8 border border-[#262626] flex flex-col md:flex-row items-start md:items-center gap-4 md:justify-between">
            <div>
              <p className="font-display text-2xl uppercase">Featuring Manuel?</p>
              <p className="text-zinc-400 text-sm mt-1">Press inquiries handled within 48 hours.</p>
            </div>
            <a href={`mailto:${SOCIALS.email}`} data-testid="media-press-email" className="btn-primary">
              <Mail className="w-4 h-4" />
              {SOCIALS.email}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] p-4">
      <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">{label}</p>
      <p className="mt-1 text-sm text-white">{value}</p>
    </div>
  );
}
